import { z } from 'zod';

/** Better Auth's default bounds (emailAndPassword.minPasswordLength / maxPasswordLength). */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const OTP_LENGTH = 6;

export const emailSchema = z.email('Enter a valid email address');

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`);

/** 6-digit numeric code (email OTP and TOTP). */
export const otpCodeSchema = z.string().regex(new RegExp(`^\\d{${OTP_LENGTH}}$`), `Enter the ${OTP_LENGTH}-digit code`);

export const signUpInputSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  // A checkbox holds a boolean; "checked" is the only acceptable value.
  acceptedTerms: z.boolean().refine((v) => v, 'You must agree to the Terms of Service and Privacy Policy'),
});

export const signInInputSchema = z.object({
  email: emailSchema,
  // Not length-checked on sign-in: the server is the judge, and old passwords must still work.
  password: z.string().min(1, 'Enter your password'),
  rememberMe: z.boolean().optional(),
});

export const verifyEmailInputSchema = z.object({ email: emailSchema, otp: otpCodeSchema });

export const requestPasswordResetInputSchema = z.object({ email: emailSchema });

export const resetPasswordInputSchema = z
  .object({
    email: emailSchema,
    otp: otpCodeSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/** Staff invite acceptance: the emailed link carries a token, the invitee only chooses a password. */
export const acceptInviteInputSchema = z
  .object({
    token: z.string().min(1),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const totpInputSchema = z.object({ code: otpCodeSchema, trustDevice: z.boolean().optional() });

export const backupCodeInputSchema = z.object({
  code: z.string().trim().min(1, 'Enter a backup code'),
  trustDevice: z.boolean().optional(),
});

export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'New password must be different from the current one',
    path: ['newPassword'],
  });

export type SignUpInput = z.infer<typeof signUpInputSchema>;
export type SignInInput = z.infer<typeof signInInputSchema>;
export type VerifyEmailInput = z.infer<typeof verifyEmailInputSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;
export type AcceptInviteInput = z.infer<typeof acceptInviteInputSchema>;
export type TotpInput = z.infer<typeof totpInputSchema>;
export type BackupCodeInput = z.infer<typeof backupCodeInputSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;
